import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ticketingAPI } from '../api/ticketing.api';
import type { TicketUser } from '../types/ticketing.types';
import './MentionInput.css';

interface MentionInputProps {
  value: string; // Storage format: @[Name](uuid)
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}

// Convert storage format to display format
// @[Laurian Detiffe](uuid) -> @Laurian Detiffe
const toDisplayFormat = (text: string): string => {
  return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
};

// Convert display format back to storage format using a mapping
const toStorageFormat = (
  displayText: string,
  mentionMap: Map<string, string>
): string => {
  let result = displayText;
  // Sort by length descending to replace longer names first
  const sortedNames = Array.from(mentionMap.keys()).sort(
    (a, b) => b.length - a.length
  );
  for (const displayName of sortedNames) {
    const storageFormat = mentionMap.get(displayName)!;
    // Use word boundary to avoid partial replacements
    const regex = new RegExp(`@${displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w])`, 'g');
    result = result.replace(regex, storageFormat);
  }
  return result;
};

export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder = 'Ecrire un commentaire...',
  disabled = false,
  rows = 1,
}) => {
  const [users, setUsers] = useState<TicketUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<TicketUser[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Map of display names to storage format for conversion
  const [mentionMap, setMentionMap] = useState<Map<string, string>>(new Map());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Display value (what user sees)
  const displayValue = useMemo(() => toDisplayFormat(value), [value]);

  // Build initial mention map from value
  useEffect(() => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const newMap = new Map<string, string>();
    let match;
    while ((match = mentionRegex.exec(value)) !== null) {
      const displayName = match[1];
      const fullMention = match[0];
      newMap.set(displayName, fullMention);
    }
    if (newMap.size > 0) {
      setMentionMap((prev) => {
        const merged = new Map(prev);
        newMap.forEach((v, k) => merged.set(k, v));
        return merged;
      });
    }
  }, [value]);

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await ticketingAPI.getUsers();
        if (response.success) {
          setUsers(response.data);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(term) ||
          user.last_name?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term)
      );
      setFilteredUsers(filtered);
      setSelectedIndex(0);
    } else {
      setFilteredUsers(users);
      setSelectedIndex(0);
    }
  }, [searchTerm, users]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDisplayValue = e.target.value;
      const cursorPosition = e.target.selectionStart;

      // Convert display value to storage format
      const newStorageValue = toStorageFormat(newDisplayValue, mentionMap);
      onChange(newStorageValue);

      // Check if we're typing an @ mention
      const textBeforeCursor = newDisplayValue.slice(0, cursorPosition);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      if (lastAtIndex !== -1) {
        // Check if this @ is at start or after a space (not in the middle of a word)
        const charBefore = textBeforeCursor[lastAtIndex - 1];
        if (lastAtIndex === 0 || charBefore === ' ' || charBefore === '\n') {
          // Check if there's no space between @ and cursor
          const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
          if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
            // Make sure this isn't an existing mention
            const existingMention = Array.from(mentionMap.keys()).find(
              (name) => textAfterAt === name || name.startsWith(textAfterAt)
            );
            if (!existingMention || textAfterAt.length < existingMention.length) {
              setMentionStartIndex(lastAtIndex);
              setSearchTerm(textAfterAt);
              setShowDropdown(true);
              return;
            }
          }
        }
      }

      setShowDropdown(false);
      setMentionStartIndex(null);
      setSearchTerm('');
    },
    [onChange, mentionMap]
  );

  const insertMention = useCallback(
    (user: TicketUser) => {
      if (mentionStartIndex === null || !textareaRef.current) return;

      const displayName = `${user.first_name} ${user.last_name}`.trim();
      const storageMention = `@[${displayName}](${user.id})`;
      const displayMention = `@${displayName}`;

      // Update mention map
      setMentionMap((prev) => {
        const newMap = new Map(prev);
        newMap.set(displayName, storageMention);
        return newMap;
      });

      // Insert into display value
      const beforeMention = displayValue.slice(0, mentionStartIndex);
      const afterMention = displayValue.slice(
        mentionStartIndex + 1 + searchTerm.length
      );

      const newDisplayValue = beforeMention + displayMention + ' ' + afterMention;

      // Convert to storage format with updated map
      const updatedMap = new Map(mentionMap);
      updatedMap.set(displayName, storageMention);
      const newStorageValue = toStorageFormat(newDisplayValue, updatedMap);

      onChange(newStorageValue);

      setShowDropdown(false);
      setMentionStartIndex(null);
      setSearchTerm('');

      // Set cursor position after the mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + displayMention.length + 1;
          textareaRef.current.selectionStart = newCursorPos;
          textareaRef.current.selectionEnd = newCursorPos;
          textareaRef.current.focus();
        }
      }, 0);
    },
    [mentionStartIndex, searchTerm, displayValue, onChange, mentionMap]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (showDropdown && filteredUsers.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredUsers.length - 1 ? prev + 1 : 0
          );
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredUsers.length - 1
          );
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(filteredUsers[selectedIndex]);
          return;
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setShowDropdown(false);
          return;
        }
      }

      // Call parent onKeyDown for other cases (like submit on Enter)
      if (!showDropdown && onKeyDown) {
        onKeyDown(e);
      }
    },
    [showDropdown, filteredUsers, selectedIndex, insertMention, onKeyDown]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const selectedItem = dropdownRef.current.querySelector(
        '.mention-item.selected'
      );
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="mention-input-container">
      <textarea
        ref={textareaRef}
        className="mention-input"
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
      />

      {showDropdown && filteredUsers.length > 0 && (
        <div ref={dropdownRef} className="mention-dropdown">
          {filteredUsers.slice(0, 8).map((user, index) => (
            <div
              key={user.id}
              className={`mention-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => insertMention(user)}
            >
              <div className="mention-item-avatar">
                {getInitials(user.first_name, user.last_name)}
              </div>
              <div className="mention-item-info">
                <div className="mention-item-name">
                  {user.first_name} {user.last_name}
                </div>
                <div className="mention-item-email">{user.email}</div>
              </div>
              <div className={`mention-item-role ${user.role}`}>{user.role}</div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && filteredUsers.length === 0 && (
        <div ref={dropdownRef} className="mention-dropdown">
          <div className="mention-no-results">Aucun utilisateur trouve</div>
        </div>
      )}
    </div>
  );
};

export default MentionInput;
